import {LocalStorageService} from 'app/core/services/local-storage.service';
import {AuthServiceHelper} from 'app/core/services/auth.service.helper';
import moment from 'moment';
import {environment} from '../../../../../environments/environment';

interface CacheRecord {
    tte: number;
    result: Record<any, any>;
}

const cachePrefix = 'apd_cache_';

export class ApiLocalCache {
    constructor(
        private localStorage: LocalStorageService,
        private auth: AuthServiceHelper
    ) {
        if (environment.production) {
            this.init();
        }
    }

    init() {
        // clean up cache every 5 minutes
        setInterval(
            () => {
                try {
                    this.cleanUp();
                } catch (e) {
                    console.log(`['ApiLocalCache'] cleanUp error`, e);
                }
            },
            5 * 60 * 1000
        );
    }

    cacheHours(queryOptions, hoursToKeep: number, result: unknown) {
        const key = this.key(queryOptions);
        const tte = moment().add({hour: hoursToKeep}).unix();
        this.localStorage.setItem(key, JSON.stringify({tte, result}));
    }

    getFromCache(queryOptions): unknown {
        const key = this.key(queryOptions);
        const cache = this.unpackCacheByKey(key);
        return cache ? cache.result : null;
    }

    private unpackCacheByKey(key: string): CacheRecord | null {
        const now = moment().unix();
        const cache = this.localStorage.getJson(key) as Record<string, any>;
        if (!cache || !cache.tte || !cache.result) {
            return null;
        }
        if (now > cache.tte) {
            this.localStorage.removeItem(key);
            return null;
        }
        return cache as CacheRecord;
    }

    private key(queryOptions) {
        return cachePrefix + hashCode(JSON.stringify(queryOptions)) + this.auth.currentUser?.id;
    }

    private cleanUp() {
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(cachePrefix)) {
                this.unpackCacheByKey(key);
            }
        });
    }
}

/**
 * Returns a hash code from a string
 *
 * @see http://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
 */
function hashCode(str): string {
    let hash = 0;
    for (let i = 0, len = str.length; i < len; i++) {
        let chr = str.charCodeAt(i);
        // eslint-disable-next-line no-bitwise
        hash = (hash << 5) - hash + chr;
        // eslint-disable-next-line no-bitwise
        hash |= 0; // Convert to 32bit integer
    }
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return hash.toString(32);
}
